<template>
  <pwa-layout title="Bons Carburant" subtitle="GESTION DE FLOTTE" :show-back="true">

    <div class="pwa-vouchers">

      <!-- ── Partie fixe ── -->
      <div class="pwa-fixed-top">
        <!-- Stats -->
        <div class="pwa-stats-row">
          <div class="pwa-stat-card pwa-stat-card--dark">
            <div class="pwa-stat-row">
              <div>
                <div class="pwa-stat-label">Total Bons</div>
                <div class="pwa-stat-value">{{ statsData.total }}</div>
                <div class="pwa-stat-sub">bons émis</div>
              </div>
              <span class="mdi mdi-file-document-outline pwa-stat-icon"></span>
            </div>
          </div>
          <div class="pwa-stat-card">
            <div class="pwa-stat-row">
              <div>
                <div class="pwa-stat-label" style="color:var(--pwa-success)">Actifs</div>
                <div class="pwa-stat-value" style="color:var(--pwa-success)">{{ statsData.active }}</div>
                <div class="pwa-stat-sub">en cours</div>
              </div>
              <span class="mdi mdi-check-circle-outline pwa-stat-icon" style="color:var(--pwa-success)"></span>
            </div>
          </div>
        </div>

        <!-- Filtres -->
        <div class="pwa-chips">
          <button
            v-for="f in filters"
            :key="f.key"
            class="pwa-chip"
            :class="{ 'pwa-chip--active': activeFilter === f.key }"
            @click="onFilter(f.key)"
          >
            <span v-if="f.dot" class="pwa-dot" :class="'pwa-dot--' + f.dot"></span>
            {{ f.label }}
          </button>
        </div>

        <div class="pwa-list-title-row">
          <span class="pwa-list-heading">Liste des bons</span>
          <span class="pwa-list-count">{{ statsData.total }}</span>
        </div>
      </div>

      <!-- ── Liste scrollable ── -->
      <div ref="listEl" class="pwa-voucher-list" @scroll="onScroll">
        <div v-if="loading && !vouchers.length" class="pwa-loading">
          <span class="mdi mdi-loading mdi-spin"></span>
        </div>
        <template v-else>
          <div v-for="v in filtered" :key="v.id" class="pwa-voucher-card">

            <!-- Cercle de progression + icône pompe -->
            <div class="pwa-progress-ring">
              <svg width="52" height="52" viewBox="0 0 52 52">
                <!-- Piste de fond -->
                <circle cx="26" cy="26" r="20" fill="none" stroke="#E8EDF4" stroke-width="3.5"/>
                <!-- Arc de progression -->
                <circle
                  cx="26" cy="26" r="20"
                  fill="none"
                  :stroke="progressColor(v)"
                  stroke-width="3.5"
                  stroke-linecap="round"
                  :stroke-dasharray="circumference"
                  :stroke-dashoffset="progressOffset(v)"
                  transform="rotate(-90 26 26)"
                />
              </svg>
              <!-- Icône pompe centrée -->
              <span class="mdi mdi-gas-station pwa-ring-icon" :style="{ color: progressColor(v) }"></span>
            </div>

            <div class="pwa-voucher-info">
              <div class="pwa-voucher-header">
                <span class="pwa-voucher-reg">{{ vehicleReg(v) }}</span>
                <span class="pwa-status-chip" :class="statusChipClass(v)">{{ statusLabel(v) }}</span>
              </div>
              <div class="pwa-voucher-driver">
                <span class="mdi mdi-account-outline" style="font-size:12px;margin-right:3px"></span>
                {{ driverName(v) }}
              </div>
              <div class="pwa-voucher-meta">
                <span class="pwa-meta-item">
                  <span class="mdi mdi-water-outline"></span>
                  {{ v.purchase_liters ? Number(v.purchase_liters).toFixed(0) + ' L' : '—' }}
                </span>
                <span class="pwa-meta-item">
                  <span class="mdi mdi-calendar-outline"></span>
                  {{ v.created_at ? formatDate(v.created_at) : '—' }}
                </span>
              </div>
            </div>

            <div class="pwa-voucher-right">
              <div class="pwa-voucher-amount">{{ formatAmount(v.purchase_amount) }}</div>
              <div class="pwa-voucher-currency">F CFA</div>
              <div v-if="hasRemaining(v)" class="pwa-voucher-remaining">
                reste {{ formatAmount(v.remaining_amount) }} F
              </div>
            </div>
          </div>

          <div v-if="loadingMore" class="pwa-load-more">
            <span class="mdi mdi-loading mdi-spin"></span>
          </div>

          <div v-if="!loading && !filtered.length" class="pwa-empty">Aucun bon trouvé</div>
        </template>
      </div>
    </div>

    <!-- FAB -->
    <template #fab>
      <button class="pwa-fab" @click="$router.push('/pwa/vouchers/new')">
        <span class="mdi mdi-plus"></span>
      </button>
    </template>
  </pwa-layout>
</template>

<script>
import PwaLayout from './PwaLayout.vue'
import CompanyVouchersService from '@/services/CompanyVouchersService'

const PAGE_SIZE = 15

export default {
  name: 'PwaVouchers',
  components: { PwaLayout },
  data() {
    return {
      circumference: 2 * Math.PI * 20, // r=20 → ≈ 125.7
      vouchers: [],
      statsData: { total: 0, active: 0 },
      loading: false,
      loadingMore: false,
      page: 1,
      hasMore: true,
      activeFilter: 'all',
      filters: [
        { key: 'all',     label: 'Tous' },
        { key: 'active',  label: 'Actifs',    dot: 'green' },
        { key: 'used',    label: 'Utilisés',  dot: 'grey' },
        { key: 'expired', label: 'Expirés',   dot: 'red' },
      ],
    }
  },
  computed: {
    department() {
      return this.$store.state.app.project
    },
    filtered() {
      if (this.activeFilter === 'active')  return this.vouchers.filter(v => this.isActive(v))
      if (this.activeFilter === 'used')    return this.vouchers.filter(v => this.isUsed(v))
      if (this.activeFilter === 'expired') return this.vouchers.filter(v => this.isExpired(v))
      return this.vouchers
    },
  },
  mounted() { this.loadVouchers() },
  methods: {
    async loadVouchers() {
      this.loading = true
      this.page = 1
      this.hasMore = true
      this.vouchers = []
      try {
        const params = { page: 1, page_size: PAGE_SIZE }
        if (this.department?.id) params.department = this.department.id
        const data = await CompanyVouchersService.get(this, params)
        const results = Array.isArray(data) ? data : (data?.results || [])
        this.vouchers = results
        const s = data?.stats || {}
        this.statsData = {
          total: s.total ?? data?.count ?? results.length,
          active: s.active ?? results.filter(v => this.isActive(v)).length,
        }
        this.hasMore = results.length === PAGE_SIZE
      } catch (e) { console.error(e) }
      finally { this.loading = false }
    },
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return
      this.loadingMore = true
      this.page++
      try {
        const params = { page: this.page, page_size: PAGE_SIZE }
        if (this.department?.id) params.department = this.department.id
        const data = await CompanyVouchersService.get(this, params)
        const results = Array.isArray(data) ? data : (data?.results || [])
        this.vouchers.push(...results)
        this.hasMore = results.length === PAGE_SIZE
      } catch (e) { console.error(e); this.page-- }
      finally { this.loadingMore = false }
    },
    onScroll() {
      const el = this.$refs.listEl
      if (!el) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) this.loadMore()
    },
    onFilter(key) {
      this.activeFilter = key
    },
    isActive(v)   { return !this.isUsed(v) && !this.isExpired(v) },
    isUsed(v)     { return v.is_used || v.status === 'used' || v.status === 'closed' },
    isExpired(v)  { return v.is_expired || v.status === 'expired' },
    statusLabel(v) {
      if (this.isExpired(v)) return 'EXPIRÉ'
      if (this.isUsed(v))    return 'UTILISÉ'
      return 'ACTIF'
    },
    statusChipClass(v) {
      if (this.isExpired(v)) return 'pwa-chip-expired'
      if (this.isUsed(v))    return 'pwa-chip-used'
      return 'pwa-chip-active'
    },
    voucherIcon(v) {
      if (this.isUsed(v))    return 'mdi-check'
      if (this.isExpired(v)) return 'mdi-clock-alert-outline'
      return 'mdi-gas-station'
    },
    voucherIconClass(v) {
      if (this.isExpired(v)) return 'pwa-vi--red'
      if (this.isUsed(v))    return 'pwa-vi--grey'
      return 'pwa-vi--green'
    },
    vehicleReg(v) {
      return v.vehicle_detail?.registration_number || v.vehicle_registration || '—'
    },
    driverName(v) {
      const d = v.vehicle_detail || v
      const name = `${d.driver_first_name || ''} ${d.driver_last_name || ''}`.trim()
      // Si pas de conducteur → afficher la plaque du véhicule
      return name || this.vehicleReg(v)
    },
    hasRemaining(v) {
      return v.remaining_amount != null && Number(v.remaining_amount) < Number(v.purchase_amount)
    },
    progressRatio(v) {
      const total = Number(v.purchase_amount) || 0
      const remaining = Number(v.remaining_amount ?? v.purchase_amount) || 0
      if (!total) return 1
      return Math.min(1, Math.max(0, remaining / total))
    },
    progressOffset(v) {
      return this.circumference * (1 - this.progressRatio(v))
    },
    progressColor(v) {
      if (this.isExpired(v)) return '#8892A4'
      if (this.isUsed(v))    return '#8892A4'
      const ratio = this.progressRatio(v)
      if (ratio <= 0.2) return '#F44336'   // rouge : presque vide
      if (ratio <= 0.5) return '#FF8C00'   // orange : mi-chemin
      return '#00B4D8'                      // cyan accent : bon état
    },
    formatAmount(val) {
      return Math.round(Number(val || 0)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
    },
    formatDate(d) {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    },
  },
}
</script>

<style scoped>
.pwa-vouchers {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - var(--pwa-header-h) - var(--pwa-nav-h) - 8px);
  overflow: hidden;
}

.pwa-fixed-top {
  flex-shrink: 0;
  padding: 16px 16px 0;
  background: var(--pwa-bg);
}

.pwa-voucher-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  min-height: 0;
}

/* Stats */
.pwa-stats-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
.pwa-stat-card { background: var(--pwa-white); border-radius: var(--pwa-card-radius); padding: 16px; }
.pwa-stat-card--dark { background: var(--pwa-dark); }
.pwa-stat-card--dark .pwa-stat-label,
.pwa-stat-card--dark .pwa-stat-value,
.pwa-stat-card--dark .pwa-stat-sub,
.pwa-stat-card--dark .pwa-stat-icon { color: white !important; }
.pwa-stat-row { display: flex; justify-content: space-between; align-items: flex-start; }
.pwa-stat-label { font-size: 11px; font-weight: 600; color: var(--pwa-muted); letter-spacing: 0.5px; margin-bottom: 4px; }
.pwa-stat-value { font-size: 28px; font-weight: 700; color: var(--pwa-text); line-height: 1; }
.pwa-stat-sub { font-size: 12px; color: var(--pwa-muted); margin-top: 2px; }
.pwa-stat-icon { font-size: 28px; opacity: 0.3; }

/* Chips */
.pwa-chips { display: flex; gap: 8px; margin-bottom: 12px; overflow-x: auto; scrollbar-width: none; }
.pwa-chips::-webkit-scrollbar { display: none; }
.pwa-chip {
  flex-shrink: 0; padding: 7px 14px; border-radius: 20px;
  border: 1.5px solid #E0E6ED; background: var(--pwa-white);
  font-size: 13px; font-weight: 500; color: var(--pwa-muted);
  cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s;
}
.pwa-chip--active { background: var(--pwa-dark); border-color: var(--pwa-dark); color: white; }
.pwa-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.pwa-dot--green { background: var(--pwa-success); }
.pwa-dot--grey  { background: var(--pwa-muted); }
.pwa-dot--red   { background: var(--pwa-error); }

.pwa-list-title-row { display: flex; align-items: center; gap: 8px; padding-bottom: 10px; }
.pwa-list-heading { font-size: 16px; font-weight: 700; color: var(--pwa-text); }
.pwa-list-count { font-size: 12px; font-weight: 600; background: var(--pwa-accent); color: white; border-radius: 10px; padding: 1px 8px; }

/* Voucher card */
.pwa-voucher-card {
  background: var(--pwa-white); border-radius: var(--pwa-card-radius);
  padding: 14px 12px 14px 14px; margin-bottom: 10px;
  display: flex; align-items: center; gap: 12px;
}

/* Cercle de progression */
.pwa-progress-ring {
  position: relative;
  width: 52px; height: 52px;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.pwa-progress-ring svg {
  position: absolute; top: 0; left: 0;
}
.pwa-ring-icon {
  position: relative;
  font-size: 20px;
  z-index: 1;
}

.pwa-voucher-info { flex: 1; min-width: 0; }
.pwa-voucher-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
.pwa-voucher-reg { font-size: 15px; font-weight: 700; color: var(--pwa-text); }
.pwa-voucher-driver { font-size: 12px; color: var(--pwa-muted); margin-bottom: 5px; display: flex; align-items: center; }
.pwa-voucher-meta { display: flex; gap: 12px; }
.pwa-meta-item { display: flex; align-items: center; gap: 4px; font-size: 12px; color: var(--pwa-muted); }
.pwa-meta-item .mdi { font-size: 13px; }

.pwa-voucher-right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
.pwa-voucher-amount { font-size: 15px; font-weight: 700; color: var(--pwa-text); white-space: nowrap; }
.pwa-voucher-currency { font-size: 11px; color: var(--pwa-muted); }
.pwa-voucher-remaining { font-size: 10px; color: var(--pwa-accent); font-weight: 600; margin-top: 2px; white-space: nowrap; }

/* Status chips */
.pwa-status-chip { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 6px; letter-spacing: 0.5px; }
.pwa-chip-active  { background: #E8F5E9; color: #2E7D32; border: 1px solid #A5D6A7; }
.pwa-chip-used    { background: #F0F4F8; color: var(--pwa-muted); border: 1px solid #D1D9E6; }
.pwa-chip-expired { background: #FFEBEE; color: #C62828; border: 1px dashed #FFCDD2; }

.pwa-loading  { text-align: center; padding: 32px; font-size: 24px; color: var(--pwa-muted); }
.pwa-load-more { text-align: center; padding: 16px; font-size: 22px; color: var(--pwa-muted); }
.pwa-empty    { text-align: center; padding: 32px; color: var(--pwa-muted); font-size: 14px; }

.pwa-fab {
  position: absolute;
  bottom: calc(var(--pwa-nav-h) + 16px);
  right: 20px;
  width: 52px; height: 52px;
  border-radius: 16px;
  background: var(--pwa-dark);
  color: white;
  border: none;
  font-size: 28px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  cursor: pointer;
  z-index: 50;
}
</style>
